import React, { useRef } from "react";
import { MenuDownIcon } from "assests/GmsIcons";
import DatePicker from 'react-datepicker';
import { seasonalPeriods } from "../../ARSConstants";

const DateRangeWithoutYearPicker = ({
    fromDate,
    toDate,
    onFromDateChange,
    onToDateChange,
    fromDateClassName = "",
    toDateClassName = ""
}: {
    fromDate: Date | null;
    toDate: Date | null;
    onFromDateChange: (date: Date | null) => void;
    onToDateChange: (date: Date | null) => void;
    fromDateClassName?: string;
    toDateClassName?: string;
}) => {
    const fromDatePickerRef = useRef<HTMLDivElement>(null);
    const toDatePickerRef = useRef<HTMLDivElement>(null);

    const monthNames = seasonalPeriods.filter(period => period !== 'Custom Date');
    const currentYear = new Date().getFullYear();

    const formatMonthDay = (date: Date | null): string => {
        if (!date) return '';
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    return (
        <div className="tw-mt-4">
            <div className="tw-flex tw-justify-start tw-gap-6">
                <div>
                    <div className="tw-text-gray-400 tw-text-xs tw-font-semibold tw-mb-2">From</div>
                    <div className="tw-relative" ref={fromDatePickerRef}>
                        <DatePicker
                            selected={fromDate}
                            onChange={(date: Date | null) => {
                                if (date) {
                                    const newDate = new Date(date);
                                    newDate.setFullYear(currentYear);
                                    onFromDateChange(newDate);

                                    if (toDate && newDate > toDate) {
                                        onToDateChange(newDate);
                                    }
                                } else {
                                    onFromDateChange(null);
                                }
                            }}
                            selectsStart
                            startDate={fromDate}
                            endDate={toDate}
                            dateFormat="MMMM d"
                            placeholderText="Select"
                            className={`tw-w-24 tw-px-2.5 tw-py-2 tw-bg-white tw-rounded tw-outline tw-outline-1 tw-outline-offset-[-1px] ${fromDateClassName || "tw-outline-gray-200"} tw-inline-flex tw-justify-start tw-items-center tw-gap-2.5 tw-text-blue-950 tw-text-sm tw-font-semibold tw-cursor-pointer`}
                            calendarClassName="tw-shadow-[0px_2px_4px_0px_rgba(0,0,0,0.25)] custom-calendar"
                            showPopperArrow={false}
                            popperProps={{ strategy: 'fixed' }}
                            showMonthDropdown
                            useShortMonthInDropdown
                            fixedHeight
                            showYearDropdown={false}
                            minDate={new Date(currentYear, 0, 1)}
                            maxDate={new Date(currentYear, 11, 31)}
                            renderCustomHeader={({
                                date,
                                changeMonth,
                                decreaseMonth,
                                increaseMonth,
                                prevMonthButtonDisabled,
                                nextMonthButtonDisabled,
                            }) => (
                                <div className="tw-flex tw-justify-between tw-items-center tw-px-2 tw-py-2">
                                    <button data-testid="seasonal-daterangewithoutyearpicker-button-1"
                                        aria-label="Previous month"
                                        onClick={decreaseMonth}
                                        disabled={prevMonthButtonDisabled}
                                        type="button"
                                        className="tw-p-1"
                                    >
                                        <svg className="tw-w-4 tw-h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M15 18l-6-6 6-6" />
                                        </svg>
                                    </button>
                                    <select data-testid="seasonal-daterangewithoutyearpicker-select-2"
                                        value={date.getMonth()}
                                        onChange={({ target: { value } }) => changeMonth(parseInt(value))}
                                        className="tw-bg-white tw-border tw-border-gray-200 tw-rounded tw-px-2 tw-py-1 tw-text-sm"
                                    >
                                        {monthNames.map((month, i) => (
                                            <option key={month} value={i}>
                                                {month}
                                            </option>
                                        ))}
                                    </select>
                                    <button data-testid="seasonal-daterangewithoutyearpicker-button-3"
                                        aria-label="Next month"
                                        onClick={increaseMonth}
                                        disabled={nextMonthButtonDisabled}
                                        type="button"
                                        className="tw-p-1"
                                    >
                                        <svg className="tw-w-4 tw-h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M9 18l6-6-6-6" />
                                        </svg>
                                    </button>
                                </div>
                            )}
                            customInput={
                                <div className="tw-w-full tw-min-w-[120px] tw-pl-2 tw-inline-flex tw-justify-between tw-items-center">
                                    <div className="tw-flex-1">
                                        {fromDate ? formatMonthDay(fromDate) : "Select"}
                                    </div>
                                    <MenuDownIcon />
                                </div>
                            }
                        />
                    </div>
                </div>

                <div>
                    <div className="tw-text-gray-400 tw-text-xs tw-font-semibold tw-mb-2">To</div>
                    <div className="tw-relative" ref={toDatePickerRef}>
                        <DatePicker
                            selected={toDate}
                            onChange={(date: Date | null) => {
                                if (date) {
                                    const newDate = new Date(date);
                                    newDate.setFullYear(currentYear);
                                    onToDateChange(newDate);
                                } else {
                                    onToDateChange(null);
                                }
                            }}
                            selectsEnd
                            startDate={fromDate}
                            endDate={toDate}
                            minDate={fromDate || undefined}
                            maxDate={new Date(currentYear, 11, 31)}
                            dateFormat="MMMM d"
                            placeholderText="Select"
                            className={`tw-w-24 tw-px-2.5 tw-py-2 tw-bg-white tw-rounded tw-outline tw-outline-1 tw-outline-offset-[-1px] ${toDateClassName || "tw-outline-gray-200"} tw-inline-flex tw-justify-start tw-items-center tw-gap-2.5 tw-text-blue-950 tw-text-sm tw-font-semibold tw-cursor-pointer`}
                            calendarClassName="tw-shadow-[0px_2px_4px_0px_rgba(0,0,0,0.25)] custom-calendar"
                            showPopperArrow={false}
                            popperPlacement="bottom-end"
                            popperProps={{ strategy: 'fixed' }}
                            showMonthDropdown
                            useShortMonthInDropdown
                            fixedHeight
                            showYearDropdown={false}
                            renderCustomHeader={({
                                date,
                                changeMonth,
                                decreaseMonth,
                                increaseMonth,
                                prevMonthButtonDisabled,
                                nextMonthButtonDisabled,
                            }) => (
                                <div className="tw-flex tw-justify-between tw-items-center tw-px-2 tw-py-2">
                                    <button data-testid="seasonal-daterangewithoutyearpicker-button-4"
                                        aria-label="Previous month"
                                        onClick={decreaseMonth}
                                        disabled={prevMonthButtonDisabled}
                                        type="button"
                                        className="tw-p-1"
                                    >
                                        <svg className="tw-w-4 tw-h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M15 18l-6-6 6-6" />
                                        </svg>
                                    </button>
                                    <select data-testid="seasonal-daterangewithoutyearpicker-select-5"
                                        value={date.getMonth()}
                                        onChange={({ target: { value } }) => changeMonth(parseInt(value))}
                                        className="tw-bg-white tw-border tw-border-gray-200 tw-rounded tw-px-2 tw-py-1 tw-text-sm"
                                    >
                                        {monthNames.map((month, i) => (
                                            <option key={month} value={i}>
                                                {month}
                                            </option>
                                        ))}
                                    </select>
                                    <button data-testid="seasonal-daterangewithoutyearpicker-button-6" aria-label="Next month"
                                        onClick={increaseMonth}
                                        disabled={nextMonthButtonDisabled}
                                        type="button"
                                        className="tw-p-1"
                                    >
                                        <svg className="tw-w-4 tw-h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M9 18l6-6-6-6" />
                                        </svg>
                                    </button>
                                </div>
                            )}
                            customInput={
                                <div className="tw-w-full tw-min-w-[120px] tw-inline-flex tw-justify-between tw-items-center">
                                    <div className="tw-flex-1">
                                        {toDate ? formatMonthDay(toDate) : "Select"}
                                    </div>
                                    <MenuDownIcon />
                                </div>
                            }
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DateRangeWithoutYearPicker;